import { Skeleton } from '@pcrm/fe/ui-kit';
import { useEffect, useRef, useState } from 'react';

/** Lățimea unei coli A4 la 96 dpi, adică exact ce randează motorul. */
const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;

/**
 * Coala, la scara care încape în panou.
 *
 * `<iframe srcDoc>`, nu `dangerouslySetInnerHTML`: documentul își aduce propriul `<style>`,
 * iar injectat în pagină ar fi rescris stilurile aplicației — `body`, `table`, `h1` sunt
 * selectoare care nimeresc tot ecranul.
 *
 * ⚠️ **Scara se recalculează la redimensionare**, nu o dată la montare: panoul își schimbă
 * lățimea când se deschide o bară laterală sau când se rotește un ecran, iar o scară
 * înghețată ar fi tăiat coala pe dreapta fără nicio eroare.
 */
export function PaperPreview({
  html,
  title = 'Previzualizarea facturii',
  minHeight = A4_HEIGHT_PX,
}: {
  html: string | null;
  /** Numele cadrului, pentru cititoarele de ecran — și pentru teste. */
  title?: string;
  /**
   * Cât de înaltă e coala cel puțin. O factură ocupă o pagină chiar cu trei linii; o chitanță
   * (#187) e o treime de pagină, iar o coală A4 aproape goală sub ea ar arăta a defect.
   */
  minHeight?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState(minHeight);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const fit = () => setScale(Math.min(1, container.clientWidth / A4_WIDTH_PX));
    const observer = new ResizeObserver(fit);

    observer.observe(container);
    fit();

    return () => observer.disconnect();
  }, []);

  /**
   * Înălțimea colii urmează conținutul, nu o constantă: o factură de patruzeci de linii e
   * mai lungă de o pagină, iar un `<iframe>` de fix o pagină ar fi tăiat-o tăcut.
   * `srcDoc` păstrează originea paginii, deci documentul se poate măsura.
   */
  const measure = () => {
    const body = frameRef.current?.contentDocument?.body;

    setHeight(Math.max(minHeight, body?.scrollHeight ?? minHeight));
  };

  if (html === null) {
    return (
      <Skeleton
        className="mx-auto w-full max-w-[794px]"
        style={{ height: Math.min(600, minHeight) }}
      />
    );
  }

  return (
    <div ref={containerRef} className="w-full">
      <div
        className="mx-auto origin-top overflow-hidden rounded bg-white shadow-elevation-card-rest"
        style={{ width: A4_WIDTH_PX, height, transform: `scale(${scale})` }}
      >
        <iframe
          ref={frameRef}
          title={title}
          srcDoc={html}
          onLoad={measure}
          // `sandbox` fără `allow-scripts`: documentul e al nostru, dar n-are nevoie de JS —
          // motorul îl randează oricum cu JavaScript oprit.
          sandbox="allow-same-origin"
          style={{ width: A4_WIDTH_PX, height, border: 0, display: 'block' }}
        />
      </div>
    </div>
  );
}
